import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import TextInput from '../FormInputs/TextInput/TextInput';
import Icon from '../Icons/Icon';

const SelectFieldOptions = ({
  label,
  options,
  handleLabelChange,
  handleOptionChange,
  addOption,
  removeOption,
  selectedType,
  defaultLabels = '',
  labelError = '',
  optionsError = '',
  onDragEnd,
}) => {
  const [inputValues, setInputValues] = useState(options);

  useEffect(() => {
    setInputValues(options);
  }, [options]);

  const getPlaceholder = (index) => `Option ${index + 1}`;

  const handleLocalChange = (index, value) => {
    const updated = [...inputValues];
    updated[index] = value;
    setInputValues(updated);
  };

  const handleSaveOnDragStart = (index) => {
    handleOptionChange(index, inputValues[index]);
  };

  const selectableTypes = ['FormItem::Select', 'FormItem::Multiselect', 'Select', 'Multi-select'];
  const isSelectType = selectableTypes.includes(selectedType?.value || selectedType);

  return (
    <div className="border rounded border-vamos-blue-gray mt-4 py-4 px-5.5">
      <div className="relative flex items-center">
        <h3 className="text-sm font-medium text-left mb-1">Label</h3>
        <Icon name="required_field" className="ml-1 mb-3 w-2 h-2" />
      </div>

      <TextInput
        name="customText"
        value={label}
        onChange={handleLabelChange}
        placeholder={`Example: ${defaultLabels}`}
        className="placeholder:text-sm placeholder:text-vamos-gray text-sm h-10 pl-2"
        dataTestId="custom-field-label-input"
      />

      {labelError && <p className="text-vamos-red text-left text-xs mt-1">{labelError}</p>}

      {isSelectType && (
        <div className="w-2/3 ml-auto mt-4">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="options-list">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {options.map((option, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Draggable key={`option-${index}`} draggableId={`option-${index}`} index={index}>
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`mb-4 ${snapshot.isDragging ? 'opacity-50' : 'opacity-100'}`}
                        >
                          <div className="flex flex-row">
                            <h3 className="text-sm text-left font-medium mb-1 inline-flex">Option
                              {index === 0 && (
                              <Icon
                                name="required_field"
                                className="ml-1 w-2 h-2"
                              />
                              )}
                            </h3>
                          </div>
                          <div className="flex items-center border border-vamos-blue-gray rounded h-10 w-full">
                            {/* Change order */}
                            <div
                              className="w-6.5 flex flex-shrink-0 items-center justify-center cursor-grab"
                              role="button"
                              tabIndex={0}
                              {...dragProvided.dragHandleProps}
                              onMouseDown={() => handleSaveOnDragStart(index)}
                            >
                              <Icon name="drag_indicator" />
                            </div>
                            {/* Enter text */}
                            <div className="flex-grow">
                              <TextInput
                                name="customText"
                                value={inputValues[index]}
                                onChange={(e) => handleLocalChange(index, e.target.value)}
                                onBlur={() => handleOptionChange(index, inputValues[index])}
                                placeholder={getPlaceholder(index)}
                                className="w-full placeholder:text-vamos-gray text-sm bg-vamos-white h-10 rounded-none px-3 outline-none border border-vamos-blue-gray focus:border-vamos-blue-gray focus:ring-0"
                                dataTestId={`custom-field-option-input-${index}`}
                              />
                            </div>
                            {/* Delete option */}
                            <button
                              aria-label="Remove option"
                              type="button"
                              onClick={() => removeOption(index)}
                              className="w-7 flex-shrink-0 flex items-center justify-center"
                            >
                              <Icon name="delete_forever" />
                            </button>
                          </div>
                          {index === 0 && optionsError && (
                            <p className="text-vamos-red text-left text-xs mt-1">{optionsError}</p>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <button
            type="button"
            onClick={addOption}
            className="h-10 border border-vamos-black px-3 py-1.5 text-base font-medium rounded w-full mt-4"
          >
            + Option
          </button>
        </div>
      )}
    </div>
  );
};

SelectFieldOptions.propTypes = {
  selectedType: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleLabelChange: PropTypes.func.isRequired,
  handleOptionChange: PropTypes.func.isRequired,
  addOption: PropTypes.func.isRequired,
  removeOption: PropTypes.func.isRequired,
  defaultLabels: PropTypes.string,
  labelError: PropTypes.string,
  optionsError: PropTypes.string,
  onDragEnd: PropTypes.func.isRequired,
};

export default SelectFieldOptions;
